import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/";

function AllSymbols() {
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // selected symbol strings for bulk delete
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchSymbols = async () => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const res = await fetch(`${BASE_URL}/GetAllSymbolsOrHolidays?type=symbol`, {
        headers: { Authorization: localStorage.getItem("SLNAdminToken") ?? "" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log("📦 GetAllSymbols response:", data);
      const list = Array.isArray(data)
        ? data
        : (data.symbols ?? data.data ?? []);
      setSymbols(list);
    } catch (err) {
      console.error("❌ Error fetching symbols:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSymbols();
  }, []);

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const getSymbolKey = (s) => s.symbol ?? s;

  const toggleOne = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === symbols.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(symbols.map(getSymbolKey)));
    }
  };

  const allChecked =
    symbols.length > 0 && selected.size === symbols.length;
  const someChecked = selected.size > 0 && !allChecked;

  // ── Delete selected ───────────────────────────────────────────────────────
  const handleDeleteSelected = async () => {
    const count = selected.size;
    const result = await Swal.fire({
      title: `Delete ${count} Symbol${count > 1 ? "s" : ""}?`,
      html: `Are you sure you want to delete <strong>${count}</strong> selected symbol${count > 1 ? "s" : ""}?<br/><span style="color:#888;font-size:13px">This action cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    setDeleting(true);
    const payload = Array.from(selected);
    console.log("📤 DeleteSymbols payload:", payload);

    try {
      const res = await fetch(`${BASE_URL}/DeleteSymbols`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("🗑️ DeleteSymbols response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      // Remove deleted from local state
      setSymbols((prev) =>
        prev.filter((s) => !selected.has(getSymbolKey(s)))
      );
      setSelected(new Set());

      await Swal.fire({
        title: "Deleted!",
        text: `${count} symbol${count > 1 ? "s" : ""} removed successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ DeleteSymbols error:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Delete single ─────────────────────────────────────────────────────────
  const handleDeleteOne = async (symbolKey) => {
    const result = await Swal.fire({
      title: "Delete Symbol?",
      html: `Are you sure you want to delete <strong>${symbolKey}</strong>?<br/><span style="color:#888;font-size:13px">This action cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    setDeleting(true);
    const payload = [symbolKey];
    console.log("📤 DeleteSymbols (single) payload:", payload);

    try {
      const res = await fetch(`${BASE_URL}/DeleteSymbols`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("🗑️ DeleteSymbols response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      setSymbols((prev) =>
        prev.filter((s) => getSymbolKey(s) !== symbolKey)
      );
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(symbolKey);
        return next;
      });

      await Swal.fire({
        title: "Deleted!",
        text: "Symbol removed successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ DeleteSymbols error:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Exchange badge colour ─────────────────────────────────────────────────
  const exchangeBadge = (exchange) => {
    const map = {
      NSE: "bg-primary-subtle text-primary border-primary-subtle",
      BSE: "bg-info-subtle text-info border-info-subtle",
      MCX: "bg-warning-subtle text-warning border-warning-subtle",
      NFO: "bg-purple-subtle text-purple border-purple-subtle",
    };
    return (
      map[exchange?.toUpperCase()] ??
      "bg-secondary-subtle text-secondary border-secondary-subtle"
    );
  };

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredSymbols = symbols.filter((s) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const symbolKey = getSymbolKey(s);
    const exchange = s.exchange ?? s.Exchange ?? symbolKey.split(":")?.[0] ?? "";
    return (
      String(symbolKey ?? "").toLowerCase().includes(q) ||
      String(exchange ?? "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredSymbols.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedSymbols = filteredSymbols.slice(startIdx, startIdx + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    All Symbols{" "}
                    {!loading && !error && (
                      <span className="text-secondary">
                        ({symbols.length})
                      </span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Manage Trading Symbols / Watchlist
                  </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  {selected.size > 0 && (
                    <button
                      className="btn btn-danger fw-normal px-4 py-1 rounded-3 d-inline-flex align-items-center gap-2"
                      onClick={handleDeleteSelected}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                      ) : (
                        <i className="bi bi-trash"></i>
                      )}
                      Delete ({selected.size})
                    </button>
                  )}
                  <Link
                    to="/manage/add-symbol"
                    className="btn btn-primary fw-normal px-5 py-1 rounded-3"
                  >
                    Add Symbols
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching symbols…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load symbols.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchSymbols}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by symbol, ticker, exchange..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredSymbols.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredSymbols.length)} of{" "}
                {filteredSymbols.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="py-2" style={{ width: "4%" }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked;
                        }}
                        onChange={toggleAll}
                        title="Select all"
                        style={{ cursor: "pointer" }}
                      />
                    </th>
                    <th className="py-2" style={{ width: "4%" }}>No.</th>
                    <th className="lh-1 py-2" style={{ width: "52%" }}>
                      Symbol Details
                      <i className="bi bi-graph-up ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Displaying Information as per Record
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Exchange
                      <i className="bi bi-building ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Market Exchange
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Action
                      <i className="bi bi-person-gear ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Role-Specific Action Panel
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSymbols.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="text-center py-5 text-secondary"
                      >
                        <i className="bi bi-graph-up fs-2 d-block mb-2"></i>
                        {symbols.length === 0
                          ? "No symbols found."
                          : "No symbols match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedSymbols.map((sym, idx) => {
                      const symbolKey = getSymbolKey(sym);
                      const exchange  = sym.exchange ?? sym.Exchange ?? symbolKey.split(":")?.[0] ?? "—";
                      const ticker    = symbolKey.split(":")?.[1]?.replace(/-EQ$|-BE$|-SM$/i, "") ?? symbolKey;
                      const segment   = symbolKey.split(":")?.[1]?.match(/-(\w+)$/)?.[1] ?? null;
                      const createdAt = sym.createdAt ?? sym.created_at ?? null;
                      const isChecked = selected.has(symbolKey);

                      return (
                        <tr
                          key={symbolKey}
                          style={{
                            opacity: deleting && isChecked ? 0.5 : 1,
                            background: isChecked ? "#f8f9ff" : undefined,
                          }}
                        >
                          {/* Checkbox */}
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isChecked}
                              onChange={() => toggleOne(symbolKey)}
                              style={{ cursor: "pointer" }}
                            />
                          </td>

                          <td>{startIdx + idx + 1}.</td>

                          {/* Symbol Details */}
                          <td className="lh-sm py-4">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold text-dark fs-6">
                                {ticker}
                              </span>
                              {segment && (
                                <span className="badge bg-light border text-secondary small px-2 rounded-3">
                                  {segment}
                                </span>
                              )}
                            </div>
                            <span className="text-dark">
                              <strong>Full Symbol:</strong>
                            </span>{" "}
                            <code className="text-secondary">{symbolKey}</code>
                            {createdAt && (
                              <>
                                <br />
                                <span className="text-dark">
                                  <strong>Added:</strong>
                                </span>{" "}
                                <span className="text-secondary small">
                                  {new Date(createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )}
                                </span>
                              </>
                            )}
                          </td>

                          {/* Exchange */}
                          <td>
                            <span
                              className={`badge border px-3 py-1 rounded-3 fw-semibold ${exchangeBadge(exchange)}`}
                            >
                              {exchange}
                            </span>
                          </td>

                          {/* Action */}
                          <td>
                            <div className="d-flex gap-1 align-items-start">
                              <button
                                className="btn p-0 border-0 bg-transparent"
                                title="Delete"
                                disabled={deleting}
                                onClick={() => handleDeleteOne(symbolKey)}
                              >
                                <div className="icon-shape icon-md bg-light-danger rounded-circle">
                                  <i className="bi bi-trash fs-4 text-dark"></i>
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && filteredSymbols.length > 0 && totalPages > 1 && (
            <nav aria-label="Symbols pagination" className="mt-3">
              <ul className="pagination justify-content-center mb-0 flex-wrap">
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {getPageNumbers().map((num) => (
                  <li
                    key={num}
                    className={`page-item ${num === safePage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default AllSymbols;
